import { pageTitle } from '@palaccount/config';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router';

import { api } from '@/api';
import { BrandMark } from '@/components/brand-mark';
import { LanguageSwitch } from '@/components/language-switch';
import { PasswordInput } from '@/components/password-input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { serverMessageKey } from '@/lib/session';

/**
 * GEN-S1 "Forgot password?", step 2 — the link's landing page.
 *
 * The token in the URL is the only credential, so this page is reachable with
 * or without a session, exactly like the activation link. Validity is checked
 * by the SERVER when the link is opened (BR-PERM-27's shape), not when it was
 * sent: a link that has expired, been used, or belongs to an account that was
 * suspended in the meantime answers the same fixed sentence.
 *
 * The two-box confirmation is client-side only, and deliberately so: the
 * server has no business being told a password twice.
 */
export function ResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    document.title = pageTitle(t('reset.setTitle'));
  }, [t]);

  const submit = async () => {
    setError(null);
    if (password !== confirm) {
      setError(t('invite.errMismatch'));
      return;
    }
    setPending(true);
    try {
      const response = await api.auth.resetPassword({ body: { token, password } });
      if (response.status !== 200) {
        const message =
          typeof response.body === 'object' && response.body !== null && 'message' in response.body
            ? String((response.body as { message: unknown }).message)
            : t('reset.errInvalid');
        const key = serverMessageKey(message);
        setError(key ? t(key) : message);
        return;
      }
      setDone(true);
    } catch {
      setError(t('reset.errInvalid'));
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-6">
      <div className="w-full max-w-md rounded-xl border bg-background p-8 shadow-sm">
        <div className="mb-6 flex items-center justify-between">
          <BrandMark />
          <LanguageSwitch />
        </div>

        {done ? (
          <div className="grid gap-4">
            <h1 className="text-xl font-bold">{t('reset.doneTitle')}</h1>
            {/* Every session ended with the reset, which is the point of one. */}
            <p className="text-sm text-muted-foreground">{t('reset.doneBody')}</p>
            <Button onClick={() => void navigate('/sign-in')}>{t('auth.signIn')}</Button>
          </div>
        ) : token === '' ? (
          <div className="grid gap-4">
            <h1 className="text-xl font-bold">{t('reset.setTitle')}</h1>
            <p role="alert" className="text-sm text-destructive">
              {t('reset.errInvalid')}
            </p>
            <Link className="text-sm text-primary underline" to="/forgot-password">
              {t('reset.askAgain')}
            </Link>
          </div>
        ) : (
          <form
            className="grid gap-4"
            onSubmit={(event) => {
              event.preventDefault();
              void submit();
            }}
          >
            <h1 className="text-xl font-bold">{t('reset.setTitle')}</h1>
            <p className="text-sm text-muted-foreground">{t('reset.setSub')}</p>
            {error ? (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            ) : null}
            <div className="grid gap-1.5">
              <Label htmlFor="reset-password">{t('reset.passwordLabel')}</Label>
              <PasswordInput
                id="reset-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="new-password"
              />
              <p className="text-xs text-muted-foreground">{t('auth.passwordHint')}</p>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="reset-confirm">{t('reset.confirmLabel')}</Label>
              <PasswordInput
                id="reset-confirm"
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
                autoComplete="new-password"
              />
            </div>
            <Button
              type="submit"
              disabled={pending || password.length === 0 || confirm.length === 0}
            >
              {pending ? <Loader2 aria-hidden className="animate-spin" /> : null}
              {t('reset.setAction')}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
